import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import CtaSection from '../components/CtaSection';
import TrustStamp from '../components/TrustStamp';

export default function Personlighedsforstyrrelser() {
  const { lang, getLocalizedValue } = useLanguage();
  const [cmsData, setCmsData] = useState(null);

  useEffect(() => {
    const fetchCmsData = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/content/personlighedsforstyrrelser_side');
        if (res.ok) {
          const items = await res.json();
          if (Array.isArray(items) && items.length > 0) {
            setCmsData(items[0]);
          }
        }
      } catch (err) {
        console.error('Error fetching personlighedsforstyrrelser_side CMS data:', err);
      }
    };
    fetchCmsData();
  }, []);

  const getCdnUrl = (url, width) => {
    if (!url) return '';
    if (url.includes('/cdn-cgi/image/')) return url;
    return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
  };

  const seoTitle = lang === 'da'
    ? 'Personlighedsforstyrrelser | Nordisk Psykiatri'
    : 'Personality Disorders | Nordisk Psykiatri';
  const seoDesc = lang === 'da'
    ? 'Læs om personlighedsforstyrrelser, symptomer, relationelle mønstre og evidensbaseret psykoterapeutisk behandling (MBT og DAT) hos Nordisk Psykiatri.'
    : 'Read about personality disorders, symptoms, relational patterns and evidence-based psychotherapy (MBT and DBT) at Nordisk Psykiatri.';

  useDocumentMetadata(seoTitle, seoDesc, { lang });

  const content = {
    da: {
      introTitle: 'Personlighedsforstyrrelser',
      introP: 'Begrebet personlighedsforstyrrelse kan opleves stigmatiserende. Det betyder ikke, at man har en ‘forstyrret personlighed’, men at bestemte mønstre i måden, man tænker, føler og indgår i relationer på, er blevet så fastlåste, at de skaber vanskeligheder i hverdagen.',
      lifeTitle: <>Livet med en<br />personlighedsforstyrrelse</>,
      lifeLead: <>En personlighedsforstyrrelse kan påvirke flere områder af livet.<br className="hidden sm:inline" /> Hvordan vanskelighederne viser sig, varierer fra person til person.</>,
      features: [
        {
          title: 'Relationer',
          desc: 'Frygt for afvisning, stærke reaktioner eller usikkerhed kan gøre det vanskeligt at skabe og bevare stabile relationer.'
        },
        {
          title: 'Følelser og selvforståelse',
          desc: 'Intense følelser, humørsvingninger eller en usikker oplevelse af, hvem man er, kan fylde meget i hverdagen.'
        },
        {
          title: 'Reaktioner og handlemønstre',
          desc: 'Nogle reagerer impulsivt og handler hurtigt på stærke følelser, mens andre præges af undgåelse, overkontrol eller et stort behov for støtte og tryghed.'
        }
      ],
      closing: 'Med den rette behandling er det muligt at forstå sine mønstre, udvikle nye strategier og få en bedre livskvalitet.',
      typesTitle: 'Fire personlighedsforstyrrelser, vi arbejder med',
      types: [
        {
          title: 'Ængstelig (evasiv)',
          desc: 'Præget af usikkerhed, frygt for kritik og en tendens til at undgå sociale situationer. Man kan længes efter nærhed, men samtidig trække sig for at beskytte sig mod afvisning.'
        },
        {
          title: 'Emotionelt ustabil',
          desc: 'Følelser og relationer kan opleves meget intenst og skifte hurtigt. Der kan samtidig være stor følsomhed over for afstand og utryghed i nære relationer.'
        },
        {
          title: 'Tvangspræget',
          desc: 'Et stærkt behov for kontrol, struktur og perfektion kan gøre fleksibilitet vanskelig. Høje krav til sig selv og andre kan skabe belastning i hverdagen og i relationer.'
        },
        {
          title: 'Dependent',
          desc: 'Et stærkt behov for støtte og tryghed kan gøre selvstændige valg svære. Frygt for at miste støtte kan gøre det vanskeligt at sige nej og stå ved egne behov.'
        }
      ],
      treatmentTitle: <>Behandling af<br />personlighedsforstyrrelser</>,
      treatmentP: 'Psykoterapi er den centrale behandling. Forløbet tilrettelægges individuelt med udgangspunkt i personlighedsmønster, aktuelle vanskeligheder, ressourcer og mål.',
      caseTitle: <>Behandlingen bygger på en<br />individuel caseformulering</>,
      caseP1: 'Valg af behandlingsfokus og metode baseres på en samlet vurdering af personlighedsfunktion, relationelle mønstre, følelsesregulering, aktuelle vanskeligheder og funktionsniveau – ikke alene på diagnosen.',
      casePills: ['Personlighedsfunktion', 'Relationelle mønstre', 'Følelsesregulering og mestring'],
      caseP2: 'Sammen med klienten opstilles konkrete mål, og behandlingens effekt følges løbende. Forløbet justeres efter respons, motivation, ressourcer og aktuelle belastninger.',
      approachesTitle: 'Terapeutiske tilgange',
      mbtTitle: 'Mentaliseringsbaseret terapi (MBT)',
      mbtDesc: 'MBT har fokus på evnen til at forstå egne og andres tanker, følelser og intentioner. Behandlingen kan være relevant ved ustabile følelser, usikker selvforståelse og tilbagevendende vanskeligheder i nære relationer.',
      datTitle: 'Dialektisk adfærdsterapi (DAT)',
      datDesc: 'DAT arbejder blandt andet med følelsesregulering, impulsivitet, krisehåndtering og relationelle færdigheder. Tilgangen kan være relevant, når intense følelser og hurtige reaktioner skaber betydelige vanskeligheder.',
      faqTitle: 'Ofte stillede spørgsmål om personlighedsforstyrrelser',
      faqList: [
        {
          q: 'Kan en personlighedsforstyrrelse ændre sig over tid?',
          a: 'Ja, absolut. Personligheden er ikke statisk, men udvikler og modner sig hele livet. Med den rette terapi oplever langt de fleste en markant bedring, og mange vil med tiden ikke længere opfylde kriterierne for diagnosen.'
        },
        {
          q: 'Hvilke typer personlighedsforstyrrelser udreder og behandler I?',
          a: 'Vi udreder og behandler en bred vifte af personlighedsstrukturer, herunder emotionelt ustabil personlighedsstruktur (borderline), ængstelig-evasiv (afvigende) personlighedsstruktur, tvangspræget og dependent personlighedsstruktur.'
        },
        {
          q: 'Hvordan foregår udredningen hos jer?',
          a: 'Udredningen foregår via grundige samtaler og strukturerede kliniske interviews (f.eks. SCID-5). Vi kortlægger dine livsmønstre, dine relationer og dine følelsesmæssige reaktioner for at give en nuanceret og præcis vurdering.'
        },
        {
          q: 'Hvor lang tid tager et behandlingsforløb?',
          a: 'Da personlighedsmønstre er dybt forankrede, strækker behandlingen sig typisk over et længere tidsrum end ved f.eks. en enkeltstående depression. Et forløb vil ofte vare fra 6 måneder til et år eller længere, afhængigt af dine behov.'
        }
      ]
    },
    en: {
      introTitle: 'Personality Disorders',
      introP: 'The term personality disorder can feel stigmatizing. It does not mean having a ‘broken personality’, but that certain patterns in how one thinks, feels, and relates to others have become so rigid that they create significant difficulties in everyday life.',
      lifeTitle: <>Living with a<br />Personality Disorder</>,
      lifeLead: <>A personality disorder can affect multiple areas of life.<br className="hidden sm:inline" /> How difficulties manifest varies from person to person.</>,
      features: [
        {
          title: 'Relationships',
          desc: 'Fear of rejection, intense reactions, or insecurity can make it difficult to form and maintain stable relationships.'
        },
        {
          title: 'Emotions and Self-Image',
          desc: 'Intense emotions, mood swings, or an unclear sense of self can occupy significant mental space in everyday life.'
        },
        {
          title: 'Reactions and Behavior Patterns',
          desc: 'Some react impulsively to strong feelings, while others are characterized by avoidance, over-control, or a strong need for reassurance.'
        }
      ],
      closing: 'With targeted treatment, it is possible to understand your patterns, develop new strategies, and achieve greater balance and well-being.',
      typesTitle: 'Four personality disorders we work with',
      types: [
        {
          title: 'Avoidant (Anxious)',
          desc: 'Characterized by deep-seated insecurity, fear of criticism, and a tendency to avoid social situations despite longing for closeness.'
        },
        {
          title: 'Emotionally Unstable (Borderline)',
          desc: 'Emotions and relationships are experienced intensely and can shift rapidly, with high sensitivity to distance and insecurity.'
        },
        {
          title: 'Obsessive-Compulsive (Anankastic)',
          desc: 'A strong need for control, order, and perfectionism that restricts flexibility and places a heavy burden on daily life.'
        },
        {
          title: 'Dependent',
          desc: 'A pronounced need for support and security can make independent decisions difficult, making it hard to set boundaries or say no.'
        }
      ],
      treatmentTitle: <>Treatment of<br />Personality Disorders</>,
      treatmentP: 'Psychotherapy is the primary treatment. The course is tailored individually based on personality patterns, current challenges, personal strengths, and goals.',
      caseTitle: <>Treatment is based on an<br />individual case formulation</>,
      caseP1: 'The choice of therapeutic focus and method is based on a comprehensive assessment of personality functioning, relational patterns, emotion regulation, current challenges, and functional level – not the diagnosis alone.',
      casePills: ['Personality functioning', 'Relational patterns', 'Emotion regulation and coping'],
      caseP2: 'Together with the client, concrete goals are established, and treatment progress is monitored continuously, adjusting to response, motivation, and life stressors.',
      approachesTitle: 'Therapeutic approaches',
      mbtTitle: 'Mentalization-Based Therapy (MBT)',
      mbtDesc: 'MBT focuses on the capacity to understand one’s own and others’ thoughts, feelings, and intentions. Particularly effective for emotional instability and relationship difficulties.',
      datTitle: 'Dialectical Behavior Therapy (DBT)',
      datDesc: 'DBT addresses emotion regulation, distress tolerance, crisis survival skills, and interpersonal effectiveness when intense emotions cause significant disruption.',
      faqTitle: 'Frequently asked questions about personality disorders',
      faqList: [
        {
          q: 'Can a personality disorder change over time?',
          a: 'Yes, absolutely. Personality is not static; it develops and matures throughout life. With appropriate psychotherapy, the vast majority experience significant improvement.'
        },
        {
          q: 'Which personality disorders do you assess and treat?',
          a: 'We assess and treat a wide range of personality patterns, including borderline (emotionally unstable), avoidant, obsessive-compulsive, and dependent personality disorders.'
        },
        {
          q: 'How does an assessment take place with you?',
          a: 'Assessment involves comprehensive clinical interviews and structured diagnostic tools (such as SCID-5), mapping out lifelong patterns and relational dynamics.'
        },
        {
          q: 'How long does a treatment course typically take?',
          a: 'Because personality patterns are deeply ingrained, therapy typically extends over a longer period than for single episodes of anxiety or depression—often 6 to 12 months or longer.'
        }
      ]
    }
  };

  const t = content[lang] || content.en || content.da;

  return (
    <div className="bg-[#f7f3ef] min-h-screen text-[#11161a] font-sans selection:bg-petrol-dark/10 selection:text-petrol-dark">
      {/* 2. Livet med en personlighedsforstyrrelse & 3-Column Timeline */}
      <section className="py-12 sm:py-20 text-center overflow-x-clip bg-[#f7f3ef]">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-6 lg:px-8">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-petrol-medium block mb-3">
            {cmsData?.intro_overskrift ? getLocalizedValue(cmsData.intro_overskrift) : t.introTitle}
          </span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark leading-[1.15] sm:leading-[1.2] mb-5 sm:mb-6 max-w-[760px] mx-auto break-words">
            {cmsData?.livet_med_overskrift ? getLocalizedValue(cmsData.livet_med_overskrift) : t.lifeTitle}
          </h1>
          <p className="font-sans text-base sm:text-lg text-[#23282a] leading-relaxed max-w-[820px] mx-auto mb-6 sm:mb-8">
            {cmsData?.livet_med_undertekst ? getLocalizedValue(cmsData.livet_med_undertekst) : t.lifeLead}
          </p>

          {/* Knapper / CTA */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8">
            {(() => {
              const bookingHref = cmsData?.hero_cta_link || cmsData?.cta_knap_link || (lang === 'da' ? "/kontakt" : "/en/contact");
              const isExt = typeof bookingHref === 'string' && (bookingHref.startsWith('http://') || bookingHref.startsWith('https://'));
              const ctaText = cmsData?.hero_cta_tekst
                ? getLocalizedValue(cmsData.hero_cta_tekst)
                : cmsData?.cta_knap_tekst
                  ? getLocalizedValue(cmsData.cta_knap_tekst)
                  : (lang === 'da' ? 'Book en indledende samtale' : 'Book an initial consultation');
              return isExt ? (
                <a
                  href={bookingHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 sm:py-4 text-sm sm:text-base font-semibold hover:bg-petrol-medium transition-all duration-200 shadow-sm cursor-pointer"
                >
                  {ctaText}
                </a>
              ) : (
                <Link
                  to={bookingHref}
                  className="inline-flex items-center justify-center rounded-full bg-petrol-dark text-white px-7 py-3.5 sm:py-4 text-sm sm:text-base font-semibold hover:bg-petrol-medium transition-all duration-200 shadow-sm cursor-pointer"
                >
                  {ctaText}
                </Link>
              );
            })()}

            <Link
              to={lang === 'da' ? "/priser" : "/en/prices"}
              className="inline-flex items-center justify-center rounded-full bg-transparent hover:bg-petrol-dark/5 text-petrol-dark border border-petrol-dark/60 hover:border-petrol-dark px-7 py-3.5 sm:py-4 text-sm sm:text-base font-semibold transition-all duration-200 shadow-xs"
            >
              {lang === 'da' ? 'Se priser' : 'See prices'}
            </Link>
          </div>

          {/* Fagligt gennemgået / Review trust stamp */}
          <div className="flex justify-center mb-10 sm:mb-14">
            <TrustStamp
              className="mt-0"
              text={cmsData?.fagligt_gennemgaaet ? getLocalizedValue(cmsData.fagligt_gennemgaaet) : (lang === 'da' ? 'Fagligt gennemgået af Felix Klich, klinisk psykolog | Opdateret september 2026' : 'Clinically reviewed by Felix Klich, Clinical Psychologist | Updated September 2026')}
            />
          </div>

          {/* 3 Features with visual top timeline bar */}
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 mt-8">
            {/* Feature 1: Relationer */}
            <article className="relative z-10 px-4 flex flex-col items-center">
              <div className="h-24 flex items-center justify-center text-[#2448ff] mb-4">
                <svg viewBox="0 0 72 72" className="w-16 h-16 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="26" cy="23" r="11" />
                  <path d="M15 55V43c0-8 5-13 11-13s11 5 11 13v12" />
                  <circle cx="45" cy="23" r="10" />
                  <path d="M37 33c2-2 5-3 8-3 7 0 12 5 12 13v12" />
                </svg>
              </div>
              <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-2.5">
                {cmsData?.livet_1_titel ? getLocalizedValue(cmsData.livet_1_titel) : t.features[0].title}
              </h3>
              <p className="text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[320px]">
                {cmsData?.livet_1_tekst ? getLocalizedValue(cmsData.livet_1_tekst) : t.features[0].desc}
              </p>
            </article>

            {/* Feature 2: Følelser og selvforståelse */}
            <article className="relative z-10 px-4 flex flex-col items-center">
              <div className="h-24 flex items-center justify-center text-[#2448ff] mb-4">
                <svg viewBox="0 0 72 72" className="w-16 h-16 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M36 60S13 38 13 23c0-8 6-14 14-14 5 0 9 2 12 7 3-5 7-7 12-7 8 0 14 6 14 14 0 15-29 37-29 37Z" />
                </svg>
              </div>
              <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-2.5">
                {cmsData?.livet_2_titel ? getLocalizedValue(cmsData.livet_2_titel) : t.features[1].title}
              </h3>
              <p className="text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[320px]">
                {cmsData?.livet_2_tekst ? getLocalizedValue(cmsData.livet_2_tekst) : t.features[1].desc}
              </p>
            </article>

            {/* Feature 3: Reaktioner og handlemønstre */}
            <article className="relative z-10 px-4 flex flex-col items-center">
              <div className="h-24 flex items-center justify-center text-[#2448ff] mb-4">
                <svg viewBox="0 0 72 72" className="w-16 h-16 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M36 62V12M36 12l-8 10M36 12l8 10M36 35L16 23M16 23l3 11M16 23l12 0M36 35l20-12M56 23l-3 11M56 23H44" />
                </svg>
              </div>
              <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-2.5">
                {cmsData?.livet_3_titel ? getLocalizedValue(cmsData.livet_3_titel) : t.features[2].title}
              </h3>
              <p className="text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[320px]">
                {cmsData?.livet_3_tekst ? getLocalizedValue(cmsData.livet_3_tekst) : t.features[2].desc}
              </p>
            </article>
          </div>

          <p className="font-sans text-base sm:text-lg text-[#3f4b47] max-w-[760px] mx-auto mt-14 leading-relaxed">
            {cmsData?.livet_med_afsluttende_tekst ? getLocalizedValue(cmsData.livet_med_afsluttende_tekst) : t.closing}
          </p>
        </div>
      </section>

      {/* 3. Fire personlighedsforstyrrelser, vi arbejder med (Vertical Timeline List) */}
      <section className="py-12 sm:py-20 overflow-x-clip bg-[#fdfcfb]">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-center text-petrol-dark mb-5 sm:mb-6 break-words">
            {cmsData?.typer_overskrift ? getLocalizedValue(cmsData.typer_overskrift) : t.typesTitle}
          </h2>

          {/* Ornamental Divider */}
          <div className="relative h-6 max-w-[760px] mx-auto mb-12 flex items-center justify-center">
            <div className="absolute left-0 w-[46%] h-[1px] bg-[#728678]" />
            <div className="w-4 h-4 border-l border-t border-[#728678] rotate-45 bg-[#fdfcfb] z-10" />
            <div className="absolute right-0 w-[46%] h-[1px] bg-[#728678]" />
          </div>

          {/* Timeline Container */}
          <div className="relative max-w-[820px] mx-auto">
            <div className="space-y-10 sm:space-y-12">
              {/* Type 1: Ængstelig (evasiv) */}
              <article className="relative flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-6 sm:gap-10">
                {/* Badge circle */}
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#efedea] flex items-center justify-center text-[#2448ff] shrink-0">
                  <svg viewBox="0 0 72 72" className="w-12 h-12 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 53V31c0-10 5-17 14-17s14 7 14 17v22M14 57h44" />
                  </svg>
                </div>

                <div className="flex-1 pt-1">
                  <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-1.5">
                    {cmsData?.type_1_titel ? getLocalizedValue(cmsData.type_1_titel) : t.types[0].title}
                  </h3>
                  <p className="font-sans text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[580px] mx-auto sm:mx-0">
                    {cmsData?.type_1_tekst ? getLocalizedValue(cmsData.type_1_tekst) : t.types[0].desc}
                  </p>
                </div>
              </article>

              {/* Type 2: Emotionelt ustabil */}
              <article className="relative flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-6 sm:gap-10">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#efedea] flex items-center justify-center text-[#2448ff] shrink-0">
                  <svg viewBox="0 0 72 72" className="w-12 h-12 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 39c6-16 12-16 18 0s12 16 18 0 12-16 20 0" />
                  </svg>
                </div>

                <div className="flex-1 pt-1">
                  <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-1.5">
                    {cmsData?.type_2_titel ? getLocalizedValue(cmsData.type_2_titel) : t.types[1].title}
                  </h3>
                  <p className="font-sans text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[580px] mx-auto sm:mx-0">
                    {cmsData?.type_2_tekst ? getLocalizedValue(cmsData.type_2_tekst) : t.types[1].desc}
                  </p>
                </div>
              </article>

              {/* Type 3: Tvangspræget */}
              <article className="relative flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-6 sm:gap-10">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#efedea] flex items-center justify-center text-[#2448ff] shrink-0">
                  <svg viewBox="0 0 72 72" className="w-12 h-12 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 16h26v37M19 56h34" />
                  </svg>
                </div>

                <div className="flex-1 pt-1">
                  <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-1.5">
                    {cmsData?.type_3_titel ? getLocalizedValue(cmsData.type_3_titel) : t.types[2].title}
                  </h3>
                  <p className="font-sans text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[580px] mx-auto sm:mx-0">
                    {cmsData?.type_3_tekst ? getLocalizedValue(cmsData.type_3_tekst) : t.types[2].desc}
                  </p>
                </div>
              </article>

              {/* Type 4: Dependent */}
              <article className="relative flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-6 sm:gap-10">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#efedea] flex items-center justify-center text-[#2448ff] shrink-0">
                  <svg viewBox="0 0 72 72" className="w-12 h-12 stroke-current fill-none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="36" cy="36" r="22" />
                  </svg>
                </div>

                <div className="flex-1 pt-1">
                  <h3 className="font-display text-lg sm:text-xl font-normal text-petrol-dark mb-1.5">
                    {cmsData?.type_4_titel ? getLocalizedValue(cmsData.type_4_titel) : t.types[3].title}
                  </h3>
                  <p className="font-sans text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[580px] mx-auto sm:mx-0">
                    {cmsData?.type_4_tekst ? getLocalizedValue(cmsData.type_4_tekst) : t.types[3].desc}
                  </p>
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Behandling af personlighedsforstyrrelser (Hero with Overlapping Colored Circles) */}
      <section className="py-12 sm:py-24 border-t border-[#8fa094]/30 overflow-x-clip bg-[#f7f3ef]">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
            
            {/* Left: Copy */}
            <div className="lg:col-span-7">
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark leading-tight sm:leading-snug mb-5 sm:mb-6 break-words">
                {cmsData?.behandling_overskrift ? getLocalizedValue(cmsData.behandling_overskrift) : t.treatmentTitle}
              </h2>
              <p className="font-sans text-base sm:text-lg text-[#34484a] leading-relaxed max-w-[620px]">
                {cmsData?.behandling_tekst ? (getLocalizedValue(cmsData.behandling_tekst)?.replace(/<[^>]*>/g, '') || t.treatmentP) : t.treatmentP}
              </p>
            </div>

            {/* Right: Graphic Container with Intersecting Pastel Circles */}
            <div className="lg:col-span-5 relative h-[320px] sm:h-[400px] border-t lg:border-t-0 lg:border-l border-[#728678]/50 flex items-center justify-center">
              <div className="relative flex items-center justify-center w-[200px] sm:w-[240px] h-[280px] sm:h-[320px]">
                {/* Sage Green Circle (Top) */}
                <div className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 w-[160px] sm:w-[190px] h-[160px] sm:h-[190px] rounded-full bg-[#d8dfc8] opacity-80 mix-blend-multiply blur-[0.5px]" />
                {/* Lavender Blue Circle (Bottom) */}
                <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 w-[160px] sm:w-[190px] h-[160px] sm:h-[190px] rounded-full bg-[#cbd5f2] opacity-80 mix-blend-multiply blur-[0.5px]" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 5. Individuel Caseformulering Section */}
      <section className="py-12 sm:py-20 text-center bg-[#fdfcfb] border-y border-[#8fa094]/30 overflow-x-clip">
        <div className="mx-auto max-w-[1180px] px-5 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-petrol-dark leading-[1.15] sm:leading-[1.2] mb-5 sm:mb-6 max-w-[820px] mx-auto break-words">
            {cmsData?.case_overskrift ? getLocalizedValue(cmsData.case_overskrift) : t.caseTitle}
          </h2>
          <p className="font-sans text-base sm:text-lg text-[#23282a] leading-relaxed max-w-[930px] mx-auto mb-6">
            {cmsData?.case_tekst_1 ? getLocalizedValue(cmsData.case_tekst_1) : t.caseP1}
          </p>

          {/* Pill line divider */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3.5 my-8 text-xs sm:text-sm font-medium text-petrol-dark">
            {(cmsData?.case_pills ? getLocalizedValue(cmsData.case_pills).split(/[•·,]/).map(p => p.trim()).filter(Boolean) : t.casePills).flatMap((pill, idx) => [
              ...(idx > 0 ? [<span key={`sep-${idx}`} className="text-[#8fa094]">•</span>] : []),
              <span key={`pill-${idx}`} className="bg-[#D1E1BE] px-4 py-1.5 rounded-full">{pill}</span>
            ])}
          </div>

          <p className="font-sans text-sm sm:text-base text-[#46524e] leading-relaxed max-w-[850px] mx-auto">
            {cmsData?.case_tekst_2 ? getLocalizedValue(cmsData.case_tekst_2) : t.caseP2}
          </p>
        </div>
      </section>

      {/* 6. Terapeutiske tilgange (Alternating Gradient Full-width Rows) */}
      <section className="pt-12 sm:pt-20 overflow-x-clip bg-[#f7f3ef]">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-normal text-center text-petrol-dark mb-8 sm:mb-10 px-5 break-words">
          {cmsData?.tilgange_overskrift ? getLocalizedValue(cmsData.tilgange_overskrift) : t.approachesTitle}
        </h2>

        {/* MBT Approach Row (Soft Blue Subtle Gradient) */}
        <div className="py-14 sm:py-16" style={{ background: 'linear-gradient(90deg, #edf0f8, #e7ecf6)' }}>
          <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="font-display text-xl sm:text-2xl font-normal text-petrol-dark mb-3">
                  {cmsData?.mbt_titel ? getLocalizedValue(cmsData.mbt_titel) : t.mbtTitle}
                </h3>
                <p className="font-sans text-base sm:text-lg text-[#34484a] leading-relaxed max-w-[580px]">
                  {cmsData?.mbt_tekst ? getLocalizedValue(cmsData.mbt_tekst) : t.mbtDesc}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* DAT Approach Row (Soft Cream Subtle Gradient) */}
        <div className="py-14 sm:py-16" style={{ background: 'linear-gradient(90deg, #f1efe6, #f7f4eb)' }}>
          <div className="mx-auto max-w-[1180px] px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="md:col-start-2">
                <h3 className="font-display text-xl sm:text-2xl font-normal text-petrol-dark mb-3">
                  {cmsData?.dat_titel ? getLocalizedValue(cmsData.dat_titel) : t.datTitle}
                </h3>
                <p className="font-sans text-base sm:text-lg text-[#34484a] leading-relaxed max-w-[580px]">
                  {cmsData?.dat_tekst ? getLocalizedValue(cmsData.dat_tekst) : t.datDesc}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unified CTA Section */}
      <CtaSection 
        title={cmsData?.cta_overskrift ? getLocalizedValue(cmsData.cta_overskrift) : undefined}
        desc={cmsData?.cta_beskrivelse ? getLocalizedValue(cmsData.cta_beskrivelse) : undefined}
        buttonText={cmsData?.cta_knap_tekst ? getLocalizedValue(cmsData.cta_knap_tekst) : undefined}
        buttonLink={cmsData?.cta_knap_link || cmsData?.hero_cta_link || "/kontakt"}
      />
    </div>
  );
}
